import type { BadgeTone } from '@terra/ui';
import { formatPriceShort } from '@terra/utils';

/**
 * Rupee formatting, in one place.
 *
 * Money crosses the wire as an integer number of paise and is divided by 100
 * exactly once, here, at the moment it becomes text. Nothing upstream of this
 * file should hold a rupee float: two of them added together stop being the
 * number the ledger holds.
 */

/** Full precision, for anywhere an amount has to reconcile against a ledger. */
export const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/** Indian short form, for headline figures where the last rupee is noise. */
export const compactInr = (p: number) => formatPriceShort(p || 0);

/**
 * Where a settlement's money is. Green only once the money is genuinely the
 * seller's; the two waiting states are amber because each is something an
 * operator may have to act on.
 */
export const SETTLEMENT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_COD_REMITTANCE: 'warning',
  PENDING_REFUND_WINDOW: 'warning',
  ELIGIBLE: 'success',
  SETTLED: 'success',
  HELD: 'warning',
  REVERSED: 'danger',
};

/** Customer-facing wording for a settlement stage. */
export const SETTLEMENT_STATUS_LABEL: Record<string, string> = {
  PENDING_COD_REMITTANCE: 'Awaiting COD remittance',
  PENDING_REFUND_WINDOW: 'In return window',
  ELIGIBLE: 'Ready to settle',
  SETTLED: 'Settled',
  HELD: 'On hold',
  REVERSED: 'Reversed',
};

export const PAYOUT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  RELEASED: 'success',
  REJECTED: 'danger',
  FAILED: 'danger',
};

/** A timestamp as a date an operator can read, or an em dash when there is none. */
export const formatDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

/** Date and time, for the settlement-run log where the hour matters. */
export const formatDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
